import React from 'react';
import { CheckCircle } from 'lucide-react';

const CaseStudies = () => {
  const cases = [
    {
      title: 'Migración Cloud para Empresa Fintech',
      client: 'Banco Digital',
      description: 'Migración completa de infraestructura on-premise a AWS, reduciendo costos operativos en un 45% y mejorando la disponibilidad al 99.99%.',
      image: 'https://images.unsplash.com/photo-1563986768609-322da13575f3',
      metrics: [
        { label: 'Reducción de costos', value: '45%' },
        { label: 'Disponibilidad', value: '99.99%' },
        { label: 'Tiempo de migración', value: '3 meses' }
      ],
      tags: ['AWS', 'Cloud Migration', 'FinTech']
    },
    {
      title: 'Plataforma de IA para E-commerce',
      client: 'Retail Innovador',
      description: 'Implementación de sistema de recomendación con IA que incrementó las ventas en 35% y mejoró la retención de clientes en 28%.',
      image: 'https://images.unsplash.com/photo-1556742044-3c52d6e88c62',
      metrics: [
        { label: 'Incremento en ventas', value: '35%' },
        { label: 'Retención', value: '+28%' },
        { label: 'Usuarios activos', value: '2M+' }
      ],
      tags: ['IA', 'Machine Learning', 'E-commerce']
    },
    {
      title: 'Sistema de Gestión Multi-cloud',
      client: 'Corporación Global',
      description: 'Arquitectura multi-cloud que optimiza recursos entre AWS, Azure y GCP, logrando ahorros del 40% en costos de infraestructura.',
      image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa',
      metrics: [
        { label: 'Ahorro en costos', value: '40%' },
        { label: 'Servicios integrados', value: '50+' },
        { label: 'Tiempo de respuesta', value: '-60%' }
      ],
      tags: ['Multi-cloud', 'Azure', 'GCP']
    }
  ];

  const clientLogos = [
    { name: 'Grupo Gibraltar', logo: 'https://static.vgroup.cl/campus/2021/01/G_Logo_Identificador_Verde.png' },
    { name: 'Surmonte', logo: 'https://surmonte.cl/wp-content/uploads/logo-surmonte-landing-1.png' },
    { name: 'Colegio Cumbres', logo: 'https://www.colegiocumbres.cl/wp-content/uploads/2021/11/colegio-cumbres-logo.png' },
    { name: 'Universidad Finis Terrae', logo: 'https://terrae.uft.cl/dev-terrae_files/Logo_FINIS_institucional.png' }
  ];

  return (
    <section id="cases" className="py-24 bg-gradient-to-b from-white to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Historias de éxito
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Potenciando el éxito de empresas líderes con soluciones tecnológicas innovadoras
          </p>
        </div>

        {/* Case Studies Grid */}
        <div className="space-y-12">
          {cases.map((caseStudy, index) => (
            <div
              key={index}
              className="group bg-gradient-to-br from-white to-white rounded-2xl overflow-hidden border border-gray-200 hover:border-[#ff6b35] transition-all duration-500 hover:shadow-2xl hover:shadow-[#ff6b35]/20"
            >
              <div className="grid md:grid-cols-2 gap-8">
                {/* Image */}
                <div className="relative h-64 md:h-full overflow-hidden">
                  <img
                    src={caseStudy.image}
                    alt={caseStudy.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-gray-50 via-[#1a0f0a]/50 to-transparent"></div>
                </div>

                {/* Content */}
                <div className="p-8 md:p-12 flex flex-col justify-center">
                  <div className="flex items-center gap-3 mb-4">
                    <CheckCircle className="w-6 h-6 text-[#ff6b35]" />
                    <span className="text-[#ff6b35] font-semibold">{caseStudy.client}</span>
                  </div>

                  <h3 className="text-3xl font-bold text-gray-900 mb-4">
                    {caseStudy.title}
                  </h3>

                  <p className="text-lg text-gray-600 mb-6">
                    {caseStudy.description}
                  </p>

                  {/* Metrics */}
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    {caseStudy.metrics.map((metric, idx) => (
                      <div key={idx} className="text-center">
                        <div className="text-2xl md:text-3xl font-bold text-[#ff6b35] mb-1">
                          {metric.value}
                        </div>
                        <div className="text-xs md:text-sm text-gray-500">
                          {metric.label}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2">
                    {caseStudy.tags.map((tag, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1 bg-orange-50/50 text-[#ff6b35] text-sm rounded-full border border-[#ff6b35]/30"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Client Logos */}
        <div className="mt-20">
          <h3 className="text-2xl font-bold text-center text-gray-900 mb-10">
            Confían en nosotros
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {clientLogos.map((client, idx) => (
              <div
                key={idx}
                className="bg-white/95 rounded-lg p-6 flex items-center justify-center border border-gray-200 hover:border-[#ff6b35] transition-all duration-300 hover:shadow-lg hover:shadow-[#ff6b35]/10 min-h-[120px]"
              >
                <img 
                  src={client.logo} 
                  alt={client.name}
                  className="max-w-full max-h-[80px] w-auto h-auto object-contain"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CaseStudies;